import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  ClipboardList, Plus, AlertTriangle, CheckCircle2, Search, MapPin, Play, RotateCcw,
  MessageSquare, BellRing, CalendarClock,
} from "lucide-react";
import { api } from "../../lib/api";
import { useSiteContext } from "../../components/layout/Layout";
import { useAuth } from "../../context/AuthContext";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../../components/ui/sheet";
import { toast } from "sonner";

// Must match backend/work_orders.py.
const PRIORITIES = ["Critical", "High", "Medium", "Low"];
const STATUSES = ["Open", "In Progress", "On Hold", "Completed", "Cancelled"];
const SYSTEMS = ["Fire-Hydrant", "Sprinkler", "Detection", "Ventilation", "Tank", "Pumps", "Lifts", "Electrical", "Plumbing", "Other"];
const WRITE_ROLES = ["super_admin", "platform_support", "admin", "customer_admin", "site_manager", "building_manager", "fire_safety_officer"];
const CLOSED = ["Completed", "Cancelled"];

const PRIO_PILL = {
  Critical: "bg-red-50 text-red-700 border-red-200",
  High: "bg-orange-50 text-orange-700 border-orange-200",
  Medium: "bg-amber-50 text-amber-700 border-amber-200",
  Low: "bg-sky-50 text-sky-700 border-sky-200",
};
const STATUS_PILL = {
  Open: "bg-blue-50 text-blue-700 border-blue-200",
  "In Progress": "bg-violet-50 text-violet-700 border-violet-200",
  "On Hold": "bg-gray-100 text-gray-600 border-gray-200",
  Completed: "bg-emerald-50 text-emerald-700 border-emerald-200",
  Cancelled: "bg-gray-50 text-gray-400 border-gray-200",
};
const VIEWS = [["active", "Active"], ["overdue", "Overdue"], ["completed", "Completed"], ["all", "All"]];

const Pill = ({ tone, children }) => (
  <span className={`inline-block px-2 py-0.5 rounded-full border text-[10px] font-bold uppercase tracking-wider whitespace-nowrap ${tone}`}>{children}</span>
);
const fmtDate = (d) => (d ? new Date(d.length === 10 ? `${d}T00:00:00` : d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—");
const fmtDateTime = (d) => (d ? new Date(d).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "—");

function Kpi({ label, value, sub, tone = "text-gray-900", onClick, active }) {
  return (
    <button type="button" onClick={onClick} disabled={!onClick}
      className={`glass rounded-xl p-4 text-left border transition ${active ? "border-orange-400 ring-2 ring-orange-100" : "border-transparent"} ${onClick ? "hover:border-gray-300" : "cursor-default"}`}>
      <div className="text-[11px] uppercase tracking-wider text-gray-500 font-semibold">{label}</div>
      <div className={`text-3xl font-extrabold mt-1 tabular-nums ${tone}`}>{value ?? "—"}</div>
      {sub && <div className="text-xs text-gray-500 mt-0.5">{sub}</div>}
    </button>
  );
}

const Field = ({ label, children }) => (
  <label className="flex flex-col gap-1">
    <span className="text-xs font-semibold text-gray-600">{label}</span>
    {children}
  </label>
);

function emptyForm(siteId) {
  return { site_id: siteId || "", title: "", description: "", system: "Other", priority: "Medium", assignee: "", due_date: "", alert_id: "" };
}

export default function WorkOrders() {
  const { sites, selectedSite, selectedOrg } = useSiteContext();
  const { user } = useAuth();
  const nav = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const isPlatform = user?.role === "super_admin" || user?.role === "platform_support";
  const canWrite = WRITE_ROLES.includes(user?.role);

  const [scope, setScope] = useState("site");
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("active");
  const [prioFilter, setPrioFilter] = useState("all");
  const [sysFilter, setSysFilter] = useState("all");
  const [query, setQuery] = useState("");
  const [selectedId, setSelectedId] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState(emptyForm(selectedSite));
  const [saving, setSaving] = useState(false);
  const [comment, setComment] = useState("");

  const params = useMemo(() => {
    const p = {};
    if (isPlatform && selectedOrg) p.org_id = selectedOrg;
    if (scope === "site" && selectedSite && selectedSite !== "all") p.site_id = selectedSite;
    return p;
  }, [isPlatform, selectedOrg, scope, selectedSite]);

  const load = useCallback(async () => {
    try {
      const [list, sum] = await Promise.all([
        api.get("/work-orders", { params }),
        api.get("/work-orders/summary", { params }),
      ]);
      setRows(list.data || []);
      setSummary(sum.data);
    } catch { toast.error("Could not load work orders"); }
    finally { setLoading(false); }
  }, [params]);
  useEffect(() => { setLoading(true); load(); }, [load]);

  // ?alert_id=…&title=… from an alert's "Raise WO"; ?wo=… from an alert's linked work order.
  useEffect(() => {
    const woId = searchParams.get("wo");
    if (woId) { setSelectedId(woId); setSearchParams({}); return; }
    const title = searchParams.get("title");
    if (!title) return;
    setForm({
      ...emptyForm(searchParams.get("site_id") || selectedSite),
      title,
      description: searchParams.get("desc") || "",
      system: SYSTEMS.includes(searchParams.get("system")) ? searchParams.get("system") : "Other",
      priority: PRIORITIES.includes(searchParams.get("priority")) ? searchParams.get("priority") : "Medium",
      alert_id: searchParams.get("alert_id") || "",
    });
    setShowCreate(true);
    setSearchParams({});
  }, [searchParams, setSearchParams, selectedSite]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return rows.filter((r) => {
      if (view === "active" && CLOSED.includes(r.status)) return false;
      if (view === "overdue" && !r.overdue) return false;
      if (view === "completed" && r.status !== "Completed") return false;
      if (prioFilter !== "all" && r.priority !== prioFilter) return false;
      if (sysFilter !== "all" && r.system !== sysFilter) return false;
      if (q && !`${r.number} ${r.title} ${r.assignee}`.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [rows, view, prioFilter, sysFilter, query]);

  const viewCount = (v) => rows.filter((r) => (v === "active" ? !CLOSED.includes(r.status) : v === "overdue" ? r.overdue : v === "completed" ? r.status === "Completed" : true)).length;
  const selected = rows.find((r) => r.wo_id === selectedId) || null;
  const siteName = (id) => sites.find((s) => s.site_id === id)?.name || id;

  const replaceRow = (wo) => setRows((prev) => prev.map((r) => (r.wo_id === wo.wo_id ? wo : r)));

  const openCreate = () => { setForm(emptyForm(selectedSite)); setShowCreate(true); };
  const submit = async () => {
    if (!form.site_id) return toast.error("Choose a site");
    if (!form.title.trim()) return toast.error("Title is required");
    setSaving(true);
    try {
      const body = { ...form, due_date: form.due_date || null, alert_id: form.alert_id || null };
      const r = await api.post("/work-orders", body);
      toast.success(`${r.data.number} created`);
      setShowCreate(false);
      await load();
      setSelectedId(r.data.wo_id);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not create work order"); }
    finally { setSaving(false); }
  };

  const update = async (patch, msg) => {
    try {
      const r = await api.patch(`/work-orders/${selected.wo_id}`, patch);
      replaceRow(r.data);
      if (msg) toast.success(msg);
      api.get("/work-orders/summary", { params }).then((s) => setSummary(s.data)).catch(() => {});
    } catch (e) { toast.error(e.response?.data?.detail || "Could not update"); }
  };
  const addComment = async () => {
    if (!comment.trim()) return;
    try {
      const r = await api.post(`/work-orders/${selected.wo_id}/comments`, { text: comment.trim() });
      replaceRow(r.data);
      setComment("");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not add note"); }
  };

  const scopeName = scope === "site" && selectedSite ? siteName(selectedSite) : "All sites";
  const filtersOn = prioFilter !== "all" || sysFilter !== "all" || query;

  return (
    <div className="space-y-5" data-testid="work-orders-page">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight">Work Orders</h1>
          <p className="text-sm text-gray-500 mt-1 flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5" strokeWidth={2.5} />{scopeName}</p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={scope} onValueChange={setScope}>
            <SelectTrigger className="w-36 bg-white border-gray-200 h-9 text-sm"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white border-gray-200 text-gray-900">
              <SelectItem value="site">This site</SelectItem>
              <SelectItem value="org">All sites</SelectItem>
            </SelectContent>
          </Select>
          {canWrite && (
            <Button className="f360-gradient border-0 h-9" data-testid="create-wo-btn" onClick={openCreate}>
              <Plus className="w-4 h-4 mr-1.5" strokeWidth={2.5} /> New work order
            </Button>
          )}
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Kpi label="Open" value={summary?.open} tone="text-blue-700" sub="not started" onClick={() => setView("active")} active={view === "active"} />
        <Kpi label="In progress" value={summary?.in_progress} tone="text-violet-700" sub="incl. on hold" />
        <Kpi label="Overdue" value={summary?.overdue} tone={summary?.overdue ? "text-red-700" : "text-gray-900"} sub="past due date" onClick={() => setView("overdue")} active={view === "overdue"} />
        <Kpi label="Completed today" value={summary?.completed_today} tone="text-emerald-700" onClick={() => setView("completed")} active={view === "completed"} />
        <Kpi label="Avg. resolution" value={summary?.avg_resolution_hours != null ? `${summary.avg_resolution_hours} h` : "—"} sub="last 30 days" />
      </div>

      {/* Toolbar */}
      <div className="glass rounded-xl p-3 flex flex-wrap items-center gap-2">
        <div className="inline-flex rounded-lg bg-gray-100 p-1" role="tablist">
          {VIEWS.map(([v, label]) => (
            <button key={v} role="tab" aria-selected={view === v} onClick={() => setView(v)} data-testid={`wo-view-${v}`}
              className={`px-3 py-1.5 rounded-md text-sm font-semibold transition ${view === v ? "bg-white shadow-sm text-gray-900" : "text-gray-500 hover:text-gray-800"}`}>
              {label}<span className="ml-1.5 text-xs text-gray-400 tabular-nums">{viewCount(v)}</span>
            </button>
          ))}
        </div>
        <div className="relative flex-1 min-w-[180px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" strokeWidth={2.5} />
          <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search number, title or assignee" className="pl-9 bg-white border-gray-200 h-9" />
        </div>
        <Select value={prioFilter} onValueChange={setPrioFilter}>
          <SelectTrigger className="w-36 bg-white border-gray-200 h-9 text-sm"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-white border-gray-200 text-gray-900">
            <SelectItem value="all">All priorities</SelectItem>
            {PRIORITIES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={sysFilter} onValueChange={setSysFilter}>
          <SelectTrigger className="w-36 bg-white border-gray-200 h-9 text-sm"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-white border-gray-200 text-gray-900">
            <SelectItem value="all">All systems</SelectItem>
            {SYSTEMS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      <div className="glass rounded-xl overflow-hidden" data-testid="wo-table">
        {loading ? (
          <div className="p-6 space-y-3">{[0, 1, 2].map((i) => <div key={i} className="h-10 bg-gray-100 rounded animate-pulse" />)}</div>
        ) : filtered.length ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-[11px] uppercase tracking-wider text-gray-500 bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="text-left px-4 py-3 font-semibold">Work order</th>
                  {scope === "org" && <th className="text-left px-3 py-3 font-semibold">Site</th>}
                  <th className="text-left px-3 py-3 font-semibold">System</th>
                  <th className="text-left px-3 py-3 font-semibold">Priority</th>
                  <th className="text-left px-3 py-3 font-semibold">Status</th>
                  <th className="text-left px-3 py-3 font-semibold">Assigned to</th>
                  <th className="text-left px-3 py-3 font-semibold">Due</th>
                  <th className="text-left px-4 py-3 font-semibold">Updated</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((r) => (
                  <tr key={r.wo_id} onClick={() => setSelectedId(r.wo_id)} data-testid={`wo-row-${r.number}`}
                    className="border-t border-gray-100 hover:bg-orange-50/40 cursor-pointer">
                    <td className="px-4 py-3 max-w-sm">
                      <div className="font-semibold text-gray-900 truncate">{r.title}</div>
                      <div className="text-xs text-gray-500 font-mono flex items-center gap-1.5">
                        {r.number}{r.alert_id && <span className="inline-flex items-center gap-0.5 text-orange-600 font-sans"><BellRing className="w-3 h-3" strokeWidth={2.5} />from alert</span>}
                      </div>
                    </td>
                    {scope === "org" && <td className="px-3 py-3 text-xs text-gray-600">{siteName(r.site_id)}</td>}
                    <td className="px-3 py-3 text-xs text-gray-600">{r.system}</td>
                    <td className="px-3 py-3"><Pill tone={PRIO_PILL[r.priority]}>{r.priority}</Pill></td>
                    <td className="px-3 py-3"><div className="flex gap-1 flex-wrap"><Pill tone={STATUS_PILL[r.status]}>{r.status}</Pill>{r.overdue && <Pill tone="bg-red-50 text-red-700 border-red-200">Overdue</Pill>}</div></td>
                    <td className="px-3 py-3 text-gray-700">{r.assignee || <span className="text-gray-400">Unassigned</span>}</td>
                    <td className={`px-3 py-3 text-xs whitespace-nowrap ${r.overdue ? "text-red-700 font-semibold" : "text-gray-600"}`}>{fmtDate(r.due_date)}</td>
                    <td className="px-4 py-3 text-xs text-gray-500 whitespace-nowrap">{fmtDateTime(r.updated_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="p-10 text-center">
            <ClipboardList className="w-10 h-10 mx-auto mb-2 text-gray-300" strokeWidth={2} />
            {rows.length ? (
              <><div className="font-semibold text-gray-900">No work orders in this view</div>
                {filtersOn && <button onClick={() => { setPrioFilter("all"); setSysFilter("all"); setQuery(""); }} className="text-sm text-orange-700 mt-1 hover:underline">Clear filters</button>}</>
            ) : (
              <><div className="font-semibold text-gray-900">No work orders yet</div>
                <div className="text-sm text-gray-500 mt-1">Create one here, or use <b>Raise WO</b> on an alert.</div>
                {canWrite && <Button className="f360-gradient border-0 mt-4" onClick={openCreate}><Plus className="w-4 h-4 mr-1.5" strokeWidth={2.5} />New work order</Button>}</>
            )}
          </div>
        )}
      </div>

      {/* Detail */}
      <Sheet open={!!selected} onOpenChange={(o) => !o && setSelectedId(null)}>
        <SheetContent side="right" className="bg-white border-gray-200 text-gray-900 w-full sm:max-w-lg overflow-y-auto" data-testid="wo-detail-panel">
          {selected && (
            <>
              <SheetHeader className="border-b border-gray-200 pb-4 pr-6">
                <div className="text-xs font-mono text-gray-500">{selected.number}</div>
                <SheetTitle className="text-xl text-gray-900 leading-snug">{selected.title}</SheetTitle>
                <div className="flex gap-1.5 flex-wrap pt-1">
                  <Pill tone={PRIO_PILL[selected.priority]}>{selected.priority}</Pill>
                  <Pill tone={STATUS_PILL[selected.status]}>{selected.status}</Pill>
                  {selected.overdue && <Pill tone="bg-red-50 text-red-700 border-red-200">Overdue</Pill>}
                </div>
              </SheetHeader>

              <div className="py-4 space-y-5">
                {canWrite && (
                  <div className="flex gap-2">
                    {selected.status === "Open" && (
                      <Button className="flex-1 bg-violet-600 hover:bg-violet-700 text-white" onClick={() => update({ status: "In Progress" }, "Work started")} data-testid="start-wo-btn">
                        <Play className="w-4 h-4 mr-1.5" strokeWidth={2.5} />Start work
                      </Button>
                    )}
                    {!CLOSED.includes(selected.status) && (
                      <Button className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => update({ status: "Completed" }, `${selected.number} completed`)} data-testid="close-wo-btn">
                        <CheckCircle2 className="w-4 h-4 mr-1.5" strokeWidth={2.5} />Mark complete
                      </Button>
                    )}
                    {CLOSED.includes(selected.status) && (
                      <Button variant="outline" className="flex-1 border-gray-200" onClick={() => update({ status: "Open" }, "Reopened")} data-testid="reopen-wo-btn">
                        <RotateCcw className="w-4 h-4 mr-1.5" strokeWidth={2.5} />Reopen
                      </Button>
                    )}
                  </div>
                )}

                {selected.description && (
                  <section>
                    <div className="text-xs font-semibold text-gray-600 mb-1">Description</div>
                    <p className="text-sm text-gray-700 whitespace-pre-wrap">{selected.description}</p>
                  </section>
                )}

                <section className="grid grid-cols-2 gap-3">
                  <Field label="Status">
                    <Select value={selected.status} onValueChange={(v) => update({ status: v })} disabled={!canWrite}>
                      <SelectTrigger className="bg-gray-50 border-gray-200 h-9" data-testid="wo-status-select"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-white border-gray-200 text-gray-900">{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                    </Select>
                  </Field>
                  <Field label="Priority">
                    <Select value={selected.priority} onValueChange={(v) => update({ priority: v })} disabled={!canWrite}>
                      <SelectTrigger className="bg-gray-50 border-gray-200 h-9"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-white border-gray-200 text-gray-900">{PRIORITIES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                    </Select>
                  </Field>
                  <Field label="Assigned to">
                    <Input key={`a-${selected.wo_id}-${selected.assignee}`} defaultValue={selected.assignee} disabled={!canWrite}
                      onBlur={(e) => e.target.value.trim() !== (selected.assignee || "") && update({ assignee: e.target.value.trim() })}
                      placeholder="Name or team" className="bg-gray-50 border-gray-200 h-9" />
                  </Field>
                  <Field label="Due date">
                    <Input type="date" value={selected.due_date || ""} disabled={!canWrite}
                      onChange={(e) => update({ due_date: e.target.value || "" })} className="bg-gray-50 border-gray-200 h-9" />
                  </Field>
                </section>

                <section className="grid grid-cols-2 gap-x-3 gap-y-2 text-sm bg-gray-50 rounded-lg p-3">
                  <div><div className="text-xs text-gray-500">Site</div><div className="font-medium">{siteName(selected.site_id)}</div></div>
                  <div><div className="text-xs text-gray-500">System</div><div className="font-medium">{selected.system}</div></div>
                  <div><div className="text-xs text-gray-500">Raised by</div><div className="font-medium">{selected.created_by}</div></div>
                  <div><div className="text-xs text-gray-500">Raised</div><div className="font-medium">{fmtDateTime(selected.created_at)}</div></div>
                  {selected.completed_at && <div><div className="text-xs text-gray-500">Completed</div><div className="font-medium">{fmtDateTime(selected.completed_at)}</div></div>}
                  {selected.alert_id && (
                    <div className="col-span-2">
                      <button onClick={() => nav("/alerts")} className="text-orange-700 text-xs font-semibold inline-flex items-center gap-1 hover:underline">
                        <BellRing className="w-3.5 h-3.5" strokeWidth={2.5} />Raised from an alert — view alerts
                      </button>
                    </div>
                  )}
                </section>

                <section>
                  <div className="text-xs font-semibold text-gray-600 mb-2">Activity</div>
                  <ol className="relative border-l border-gray-200 ml-1.5 space-y-3">
                    {[...(selected.log || [])].reverse().map((e, i) => (
                      <li key={i} className="ml-4">
                        <span className={`absolute -left-[5px] mt-1.5 w-2.5 h-2.5 rounded-full ${e.comment ? "bg-orange-400" : "bg-gray-300"}`} />
                        <div className="text-xs text-gray-500">{fmtDateTime(e.at)} · <b className="text-gray-700">{e.by}</b></div>
                        <div className={`text-sm ${e.comment ? "text-gray-900 bg-orange-50 rounded-md px-2 py-1 mt-0.5" : "text-gray-700"}`}>{e.action}</div>
                      </li>
                    ))}
                  </ol>
                  {canWrite && (
                    <div className="flex gap-2 mt-3">
                      <Input value={comment} onChange={(e) => setComment(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addComment()}
                        placeholder="Add a note (e.g. parts ordered)" className="bg-gray-50 border-gray-200 h-9" data-testid="wo-comment" />
                      <Button variant="outline" className="h-9 border-gray-200" onClick={addComment} disabled={!comment.trim()}>
                        <MessageSquare className="w-4 h-4" strokeWidth={2.5} />
                      </Button>
                    </div>
                  )}
                </section>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>

      {/* Create */}
      <Sheet open={showCreate} onOpenChange={setShowCreate}>
        <SheetContent side="right" className="bg-white border-gray-200 text-gray-900 w-full sm:max-w-md overflow-y-auto" data-testid="create-wo-panel">
          <SheetHeader className="border-b border-gray-200 pb-4">
            <SheetTitle className="text-xl text-gray-900">New work order</SheetTitle>
            {form.alert_id && (
              <div className="text-xs text-orange-700 inline-flex items-center gap-1"><AlertTriangle className="w-3.5 h-3.5" strokeWidth={2.5} />Linked to the alert it was raised from</div>
            )}
          </SheetHeader>
          <div className="py-4 space-y-4">
            <Field label="Site *">
              <Select value={form.site_id || ""} onValueChange={(v) => setForm({ ...form, site_id: v })}>
                <SelectTrigger className="bg-gray-50 border-gray-200"><SelectValue placeholder="Choose site" /></SelectTrigger>
                <SelectContent className="bg-white border-gray-200 text-gray-900">{sites.map((s) => <SelectItem key={s.site_id} value={s.site_id}>{s.name}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Title *">
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="e.g. Jockey pump not cutting off" className="bg-gray-50 border-gray-200" data-testid="wo-form-title" />
            </Field>
            <Field label="Description">
              <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} placeholder="What needs doing, and where" className="bg-gray-50 border-gray-200" />
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="System">
                <Select value={form.system} onValueChange={(v) => setForm({ ...form, system: v })}>
                  <SelectTrigger className="bg-gray-50 border-gray-200"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white border-gray-200 text-gray-900">{SYSTEMS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field label="Priority">
                <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                  <SelectTrigger className="bg-gray-50 border-gray-200"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white border-gray-200 text-gray-900">{PRIORITIES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field label="Assign to">
                <Input value={form.assignee} onChange={(e) => setForm({ ...form, assignee: e.target.value })} placeholder="Name or team" className="bg-gray-50 border-gray-200" />
              </Field>
              <Field label="Due date">
                <Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} className="bg-gray-50 border-gray-200" />
              </Field>
            </div>
            <div className="flex gap-2 pt-2">
              <Button className="flex-1 f360-gradient border-0" onClick={submit} disabled={saving} data-testid="submit-wo-btn">
                <CalendarClock className="w-4 h-4 mr-1.5" strokeWidth={2.5} />{saving ? "Creating…" : "Create work order"}
              </Button>
              <Button variant="outline" className="border-gray-200" onClick={() => setShowCreate(false)}>Cancel</Button>
            </div>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}
